// SoulSearch.js — the soul search for every page outside the galaxy.
// Lives in the site header (left of the menu). Finds claimed souls by the
// person's name or their star's name and jumps straight to their memorial.
// Claims are fetched once, only when the search is first opened.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchMemorials } from '../searchMemorials';

function SoulSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Indexed search with a small debounce — reads a handful of documents no
  // matter how many memorials exist.
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      try {
        const found = await searchMemorials(q, 8);
        if (searchSeq.current === seq) {
          setResults(found.map((m) => ({
            id: m.galaxyLocation?.starId || m.id,
            firstName: m.firstName,
            lastName: m.lastName,
            starName: m.galaxyLocation?.starName || null,
            region: m.galaxyLocation?.region || null,
            memorialSlug: m.slug || null,
            living: m.preNeed && m.preNeedStatus !== 'activated',
          })));
        }
      } catch (e) {
        console.warn('SoulSearch query failed:', e);
        if (searchSeq.current === seq) setResults([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [term]);

  const close = useCallback(() => { setOpen(false); setTerm(''); }, []);

  const pick = useCallback((c) => {
    close();
    if (c.memorialSlug) navigate(`/memorial/${c.memorialSlug}`);
    else navigate(`/galaxy?star=${c.id}`);
  }, [close, navigate]);

  const searchAll = useCallback(() => {
    const q = term.trim();
    if (q) { close(); navigate(`/search?q=${encodeURIComponent(q)}`); }
  }, [term, close, navigate]);

  return (
    <div className="relative flex items-center">
      <input
        ref={inputRef}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && results.length === 0) searchAll();
          if (e.key === 'Escape') close();
        }}
        placeholder="Find a memorial or their star…"
        className={`bg-white/10 rounded-full py-2 text-sm text-white placeholder-white/40 focus:outline-none transition-all duration-300 ${
          open
            ? 'w-44 md:w-72 opacity-100 px-4 mr-2 border border-white/25 focus:border-white/50'
            : 'w-0 opacity-0 px-0 border border-transparent'
        }`}
      />
      <button
        onClick={() => (open ? close() : setOpen(true))}
        className="p-2 text-white hover:opacity-70 transition-all duration-300"
        title={open ? 'Close search' : 'Search memorials'}
        aria-label={open ? 'Close search' : 'Search memorials'}
      >
        {open ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
          </svg>
        )}
      </button>

      {open && term.trim().length >= 2 && (
        <div
          className="absolute right-0 top-12 w-72 max-h-72 overflow-y-auto bg-black/95 border border-white/20 backdrop-blur-md rounded-2xl p-2"
          style={{ zIndex: 99999 }}
        >
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <span className="block text-white text-sm">
                {c.firstName} {c.lastName}
              </span>
              {c.starName ? (
                <span className="block text-amber-100/80 text-xs mt-0.5">
                  ✦ {c.starName}{c.region ? ` · ${c.region}` : ''}
                </span>
              ) : (
                <span className="block text-white/45 text-xs mt-0.5">Memorial</span>
              )}
            </button>
          ))}
          {results.length === 0 && (
            <p className="px-3 py-2 text-white/50 text-xs">
              No memorials match yet.
            </p>
          )}
          <button
            onClick={searchAll}
            className="w-full text-left px-3 py-2 mt-1 rounded-lg text-white/70 hover:text-white hover:bg-white/10 text-xs transition-colors border-t border-white/10"
          >
            Search all memorials for “{term.trim()}” →
          </button>
        </div>
      )}
    </div>
  );
}

export default SoulSearch;
